import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import type { HostContext } from "@repo/bridge";
import { applyTheme, DEFAULT_THEME, parseTheme, type Theme } from "../theme";

/**
 * Keeps `<html data-theme>` in sync with the host's current theme.
 *
 * The channel is seeded from `?theme=` in `bootstrap`, so the first render already holds the right
 * value and the effect is a no-op against what `index.html` wrote pre-paint. An unrecognized push
 * keeps the current theme rather than snapping back to the default — a host typo should not
 * visibly re-skin the widget.
 */
export function useHostTheme(hostContext: HostContext): Theme {
  const channel = useMemo(() => hostContext.channel("theme"), [hostContext]);
  const raw = useSyncExternalStore(channel.subscribe, channel.get);

  const [theme, setTheme] = useState<Theme>(() => parseTheme(raw) ?? DEFAULT_THEME);

  useEffect(() => {
    const next = parseTheme(raw);
    if (next) {
      setTheme(next);
    } else if (raw !== undefined) {
      console.warn(`[tiger-agent] ignoring unrecognized theme from host: ${raw}`);
    }
  }, [raw]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  return theme;
}
